import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'

import EditIcon from '@mui/icons-material/Edit'

import axios from '../../dataClient/dataClient'
import { allSettingsSections } from '../../routes/settingsRoutes'
import { FlexColumn, FlexEndContainer, OutlineContainer, SpaceBetweenRowContainer, VerticallyCenteredFlexColumn } from '../styles/Containers'
import Button, { SecondaryButton } from '../common/Button'
import Loading from '../common/Loading'
import LoadingError from '../common/LoadingError'
import Breadcrumbs from '../settings/Breadcrumbs'
import DeleteCommunicationButton from '../communications/DeleteCommunicationButton'
import CommunicationTemplateTable from '../communicationTemplates/CommunicationTemplateTable'
import CommunicationTemplateEditable from '../communicationTemplates/CommunicationTemplateEditable'
import CommunicationTemplateReadOnly from '../communicationTemplates/CommunicationTemplateReadOnly'

const HeaderRow = styled(SpaceBetweenRowContainer)`
  min-height: 37px;
  align-items: center;
`

function formatCommunicationTemplate(template) {
  return { ...template, updatedAt: new Date(template.updatedAt) }
}

// Deferred so we don't read allSettingsSections during the settingsRoutes ↔ this-page circular import.
function getBaseCrumbs() {
  const { templates } = allSettingsSections
  return [
    { label: templates.label, path: templates.path },
    { label: templates.subsections.communications.label, path: templates.subsections.communications.path }
  ]
}

function SettingsTemplateCommunicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [publicConfig, setPublicConfig] = useState()
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(false)
  const [communicationTemplates, setCommunicationTemplates] = useState([])
  const [activeTemplate, setActiveTemplate] = useState()

  const baseCrumbs = getBaseCrumbs()
  const communicationTemplateId = searchParams.get('communicationTemplateId')
  const isEditing = Boolean(communicationTemplateId) && searchParams.get('edit') === 'true'
  const isCreating = searchParams.get('create') === 'true'
  const isListView = !isCreating && !communicationTemplateId
  const isDetailView = !isCreating && !isEditing && Boolean(communicationTemplateId) && Boolean(activeTemplate)

  function getPublicConfig() {
    axios
      .get('/public-config')
      .then(({ data }) => setPublicConfig(data.data))
      .catch(e => console.error('Failed to retrieve public configs', e))
  }

  function getCommunicationTemplates() {
    setListLoading(true)
    setListError(false)
    axios
      .get('/communication/communication-template')
      .then(({ data }) => setCommunicationTemplates(data.data.map(formatCommunicationTemplate)))
      .catch(e => {
        console.error('Failed to retrieve communication templates', e)
        setListError(true)
      })
      .finally(() => setListLoading(false))
  }

  function getActiveCommunicationTemplate(id) {
    setDetailLoading(true)
    setDetailError(false)
    axios
      .get(`/communication/communication-template/${id}`)
      .then(({ data }) => setActiveTemplate(formatCommunicationTemplate(data.data)))
      .catch(e => {
        console.error('Failed to retrieve communication template', id, e)
        setDetailError(true)
      })
      .finally(() => setDetailLoading(false))
  }

  const handleSelectTemplate = id => setSearchParams({ communicationTemplateId: id })
  const handleEditTemplate = id => setSearchParams({ communicationTemplateId: id, edit: 'true' })
  const handleCreateNewTemplate = () => setSearchParams({ create: 'true' })

  function handleCancelUpdateTemplate() {
    if (isEditing) setSearchParams({ communicationTemplateId })
    else setSearchParams({})
  }

  function handleUpdateTemplate(template) {
    setActiveTemplate(formatCommunicationTemplate(template))
    getCommunicationTemplates()
    setSearchParams({ communicationTemplateId: template.id })
  }

  function handleDeleteTemplate() {
    getCommunicationTemplates()
    setSearchParams({})
  }

  useEffect(() => {
    getCommunicationTemplates()
    getPublicConfig()
  }, [])

  // The active template lives in the url so the breadcrumbs & browser history can navigate back to the list.
  useEffect(() => {
    if (!communicationTemplateId) {
      setActiveTemplate()
      setDetailLoading(false)
      setDetailError(false)
      return
    }
    setActiveTemplate(prev => (prev?.id === communicationTemplateId ? prev : undefined))
    getActiveCommunicationTemplate(communicationTemplateId)
  }, [communicationTemplateId])

  let crumbs = baseCrumbs
  if (isCreating) crumbs = [...baseCrumbs, { label: 'New Template' }]
  else if (isEditing) crumbs = [...baseCrumbs, { label: 'Edit Template' }]
  else if (activeTemplate) crumbs = [...baseCrumbs, { label: activeTemplate.name }]

  if ((isListView && listLoading) || (communicationTemplateId && !isCreating && detailLoading)) {
    const loadingText = isListView && listLoading ? 'templates' : 'template'
    return (
      <FlexColumn height="100%" width="100%" gap="10px">
        <HeaderRow>
          <Breadcrumbs crumbs={baseCrumbs} />
        </HeaderRow>
        <Loading>Loading communication {loadingText}...</Loading>
      </FlexColumn>
    )
  }

  if ((isListView && listError) || (communicationTemplateId && !isCreating && detailError)) {
    const errorText = isListView && listError ? 'templates' : 'template'
    return (
      <FlexColumn height="100%" width="100%" gap="10px">
        <HeaderRow>
          <Breadcrumbs crumbs={baseCrumbs} />
        </HeaderRow>
        <VerticallyCenteredFlexColumn height="100%">
          <LoadingError message={[`There was an error loading the communication ${errorText}. Please refresh the page to try again.`]} />
        </VerticallyCenteredFlexColumn>
      </FlexColumn>
    )
  }

  return (
    <FlexColumn width="100%" gap="10px">
      {(isListView || isDetailView) && (
        <HeaderRow gap="10px">
          <Breadcrumbs crumbs={crumbs} />
          {isListView && <Button onClick={handleCreateNewTemplate}>Create New Template</Button>}
          {isDetailView && (
            <FlexEndContainer gap="5px">
              <DeleteCommunicationButton state="TEMPLATE" id={activeTemplate.id} name={activeTemplate.name} onSuccess={handleDeleteTemplate} />
              <SecondaryButton onClick={() => handleEditTemplate(activeTemplate.id)}>
                <EditIcon /> Edit Template
              </SecondaryButton>
            </FlexEndContainer>
          )}
        </HeaderRow>
      )}
      {isCreating && (
        <CommunicationTemplateEditable
          isCreating
          publicConfig={publicConfig}
          handleUpdateTemplate={handleUpdateTemplate}
          handleCancelUpdateTemplate={handleCancelUpdateTemplate}>
          <Breadcrumbs crumbs={crumbs} />
        </CommunicationTemplateEditable>
      )}
      {isEditing && activeTemplate && (
        <CommunicationTemplateEditable
          communicationTemplate={activeTemplate}
          publicConfig={publicConfig}
          handleUpdateTemplate={handleUpdateTemplate}
          handleCancelUpdateTemplate={handleCancelUpdateTemplate}>
          <Breadcrumbs crumbs={crumbs} />
        </CommunicationTemplateEditable>
      )}
      {isDetailView && (
        <OutlineContainer padding="25px 30px">
          <CommunicationTemplateReadOnly template={activeTemplate} publicConfig={publicConfig} />
        </OutlineContainer>
      )}
      {isListView && (
        <OutlineContainer padding="15px 10px">
          <CommunicationTemplateTable
            communicationTemplates={communicationTemplates}
            onEditCommunicationTemplate={handleEditTemplate}
            handleSelectTemplate={handleSelectTemplate}
          />
        </OutlineContainer>
      )}
    </FlexColumn>
  )
}

export default SettingsTemplateCommunicationsPage
